// @givmo/app-charity — URL state for the unified statement (CPA-WEB11).
//
// The whole toolbar lives in the query string, so a charity can send its bookkeeper a link to exactly
// what it is looking at — `?tab=donor&sort=amount-high&q=jane`. Following the grants-ops precedent
// (`apps/super-admin/src/features/grants-ops/grants-ops-search.ts`): a typed TOLERANT validator, so a
// stale or hand-edited link still lands on a working screen instead of a router error. Every
// unrecognized value is DROPPED rather than throwing — the URL is user-editable input, not a contract.
//
// ★ TWO SHAPES, ON PURPOSE. `StatementSearchParams` is the URL: every key optional, chips a comma
// string, defaults ABSENT. `StatementSearch` is what the screen reads: every field present, defaults
// filled in. Keeping them apart is what stops the common case from acquiring a URL full of noise —
// `?tab=all&sort=newest&q=&chips=` is the same screen as `/grants`, and only one of them is a link
// worth sending someone.
//
// `grant` is the deep-link half: `/grants/$grantId` has been a real, shareable route since CPA-WEB5
// and must keep working. It now redirects here as `?tab=grant&grant=<id>`, which opens the by-grant
// view with that grant expanded and scrolled to. Nothing 404s, and an old link still answers the
// question it was sent to answer.

import { CHIP_OPTIONS, SORT_OPTIONS, STATEMENT_TABS, STATUS_CHIP_PAIR } from './statement-vocab';
import type { StatementChip, StatementSort, StatementTab } from './statement-vocab';

/** The URL shape. Every key optional; absent means "the default". */
export interface StatementSearchParams {
  tab?: StatementTab;
  sort?: StatementSort;
  q?: string;
  /** Comma-separated chip tokens, in the canonical option order. */
  chips?: string;
  grant?: string;
}

/** The resolved shape the screen renders from. */
export interface StatementSearch {
  tab: StatementTab;
  sort: StatementSort;
  q: string;
  chips: StatementChip[];
  grant?: string;
}

export const DEFAULT_TAB: StatementTab = 'all';
export const DEFAULT_SORT: StatementSort = 'newest';

const TABS = new Set<string>(STATEMENT_TABS.map((tab) => tab.key));
const SORTS = new Set<string>(SORT_OPTIONS.map((option) => option.value));
const CHIPS = new Set<string>(CHIP_OPTIONS.map((option) => option.value));

/**
 * The route's `validateSearch`. Tolerant on every key: a bad `tab`, `sort` or chip token is dropped
 * (so it falls back to the default and stops appearing in the URL) rather than failing the route. An
 * empty `q` is dropped too — `?q=` is not a search for the empty string, and keeping the key would
 * leave it on the URL of every screen where a search had once been cleared.
 */
export function validateStatementSearch(search: Record<string, unknown>): StatementSearchParams {
  const out: StatementSearchParams = {};
  const tab = str(search.tab);
  if (tab && TABS.has(tab) && tab !== DEFAULT_TAB) out.tab = tab as StatementTab;
  const sort = str(search.sort);
  if (sort && SORTS.has(sort) && sort !== DEFAULT_SORT) out.sort = sort as StatementSort;
  const q = str(search.q);
  if (q) out.q = q;
  const chips = parseChips(search.chips);
  if (chips.length > 0) out.chips = chips.join(',');
  const grant = str(search.grant);
  if (grant) out.grant = grant;
  return out;
}

/** Fill the defaults the URL leaves out. */
export function parseStatementSearch(params: StatementSearchParams): StatementSearch {
  const resolved: StatementSearch = {
    tab: params.tab ?? DEFAULT_TAB,
    sort: params.sort ?? DEFAULT_SORT,
    q: params.q ?? '',
    chips: parseChips(params.chips),
  };
  if (params.grant) resolved.grant = params.grant;
  return resolved;
}

/** Back to the URL shape, defaults omitted. */
export function toStatementSearch(next: StatementSearch): StatementSearchParams {
  const out: StatementSearchParams = {};
  if (next.tab !== DEFAULT_TAB) out.tab = next.tab;
  if (next.sort !== DEFAULT_SORT) out.sort = next.sort;
  if (next.q.trim().length > 0) out.q = next.q.trim();
  if (next.chips.length > 0) out.chips = next.chips.join(',');
  if (next.grant) out.grant = next.grant;
  return out;
}

function str(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function parseChips(value: unknown): StatementChip[] {
  const raw = typeof value === 'string' ? value : Array.isArray(value) ? value.join(',') : '';
  const seen = new Set<string>();
  for (const part of raw.split(',')) {
    const token = part.trim().toLowerCase();
    if (CHIPS.has(token)) seen.add(token);
  }
  // `sent` and `preparing` partition one field; a URL carrying both can only match nothing, so the
  // pair is dropped rather than rendered as a false empty ledger (cert R2-B-1).
  if (STATUS_CHIP_PAIR.every((chip) => seen.has(chip))) {
    for (const chip of STATUS_CHIP_PAIR) seen.delete(chip);
  }
  // Emitted in the canonical option order, so two links selecting the same chips ARE the same link.
  return CHIP_OPTIONS.filter((option) => seen.has(option.value)).map((option) => option.value);
}

/** The expansion key the `?grant=` deep link opens — matches `buildStatementView`'s grant keys. */
export function grantExpansionKey(grantRef: string): string {
  return `grant:${grantRef}`;
}
